import { Component, ReactNode, Suspense, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { track } from "@/lib/analytics";
import { RetryButton } from "@/components/layout/RetryButton";
import { ErrorDetails, makeErrorId } from "@/components/layout/ErrorDetails";
import { OfflineFallback } from "@/components/layout/OfflineFallback";

interface BoundaryProps {
  children: ReactNode;
  onReset: () => void;
  routeLabel?: string;
  attempt: number;
}

interface BoundaryState {
  error: Error | null;
  errorId: string | null;
}

/** Coarse classification used in analytics so dashboards can group failures. */
export type LazyErrorType =
  | "chunk_load"        // dynamic import / network failure
  | "css_chunk"         // stylesheet chunk failed
  | "syntax"            // bad bundle / parse error
  | "type"              // TypeError at render time
  | "reference"         // ReferenceError at render time
  | "render";           // anything else thrown inside the tree

export function classifyLazyError(err: Error | null): LazyErrorType {
  if (!err) return "render";
  const name = err.name?.toLowerCase() ?? "";
  const msg = `${err.name} ${err.message}`.toLowerCase();
  if (msg.includes("loading css chunk")) return "css_chunk";
  if (
    msg.includes("chunkloaderror") ||
    msg.includes("loading chunk") ||
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("importing a module script failed")
  ) return "chunk_load";
  if (name.includes("syntaxerror")) return "syntax";
  if (name.includes("typeerror")) return "type";
  if (name.includes("referenceerror")) return "reference";
  return "render";
}

function isChunkLoadError(err: Error | null): boolean {
  const t = classifyLazyError(err);
  return t === "chunk_load" || t === "css_chunk";
}

class ErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null, errorId: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error, errorId: makeErrorId() };
  }

  componentDidCatch(error: Error) {
    // eslint-disable-next-line no-console
    console.error("[RouteErrorBoundary]", this.state.errorId, this.props.routeLabel ?? "", error);
    // attempt is 0-indexed (initial mount); analytics convention starts at 1.
    track("lazy_route_error", {
      route: this.props.routeLabel ?? "unknown",
      error_id: this.state.errorId,
      error_type: classifyLazyError(error),
      error_name: error.name,
      error_message: (error.message || String(error)).slice(0, 240),
      attempt: this.props.attempt + 1,
      surface: "route",
    });
  }

  reset = () => {
    this.setState({ error: null, errorId: null });
    this.props.onReset();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunk = isChunkLoadError(error);
    // If this looks like a network/chunk failure AND the device is offline,
    // show the dedicated offline fallback (auto-retries when back online)
    // instead of a generic error card.
    const offline =
      chunk && typeof navigator !== "undefined" && navigator.onLine === false;
    if (offline) {
      return (
        <OfflineFallback
          onRetry={this.reset}
          context={this.props.routeLabel ? `the ${this.props.routeLabel} page` : "this page"}
        />
      );
    }
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="border-border/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
              <div className="min-w-0">
                <CardTitle className="font-display text-lg">
                  {chunk ? "This page failed to load" : "Something went wrong"}
                </CardTitle>
                <CardDescription>
                  {chunk
                    ? "A network hiccup or an updated version of the app interrupted the download. Try again — it usually works on the second attempt."
                    : "An unexpected error happened while rendering this page."}
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ErrorDetails
              error={error}
              errorId={this.state.errorId ?? "unknown"}
              errorType={classifyLazyError(error)}
              route={this.props.routeLabel}
              attempt={this.props.attempt + 1}
            />
            <div className="flex flex-wrap gap-2">
              <RetryButton attempt={this.props.attempt + 1} onRetry={this.reset} />
              <Button variant="outline" onClick={() => window.location.reload()} className="gap-2">
                <RefreshCw className="h-4 w-4" /> Reload page
              </Button>
              <Button variant="ghost" onClick={() => (window.location.href = "/")}>
                Go home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

interface LazyRouteProps {
  fallback: ReactNode;
  children: ReactNode;
  routeLabel?: string;
}

/**
 * Wraps a lazy-loaded route in both an Error Boundary (for failed chunk
 * downloads / render errors) and a Suspense boundary (for the loading
 * skeleton). The "Try again" button bumps a key so React.lazy retries the
 * dynamic import instead of leaving the user on a blank screen.
 */
export function LazyRoute({ fallback, children, routeLabel }: LazyRouteProps) {
  const [attempt, setAttempt] = useState(0);
  const onReset = useCallback(() => setAttempt((n) => n + 1), []);
  return (
    <ErrorBoundary key={attempt} attempt={attempt} onReset={onReset} routeLabel={routeLabel}>
      <Suspense fallback={fallback}>{children}</Suspense>
    </ErrorBoundary>
  );
}
